import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, User, ArrowRight } from 'lucide-react';
import { getPublicBlogs, BlogPost as ApiBlogPost } from '../services/blog';
import Loader from '../components/Loader';
import { getAllBlogCategories, BlogCategory } from '../services/blogCategory';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  publishedDate: string;
  readTime: string;
  category: string;
  image: string;
  slug: string;
}

interface BlogPageProps {
  initialData?: {
    blogPosts?: BlogPost[];
    categories?: BlogCategory[];
  };
}

const BlogPage = ({ initialData }: BlogPageProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>(initialData?.blogPosts || []);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>(initialData?.blogPosts || []);
  const [loading, setLoading] = useState(!initialData?.blogPosts); // Don't show loading if we have initial data
  const [error, setError] = useState<string>('');
  const [categories, setCategories] = useState<BlogCategory[]>(initialData?.categories || []);

  // Load blog categories from API (only if not provided via SSR)
  useEffect(() => {
    if (!initialData?.categories) {
      async function fetchCategories() {
        const response = await getAllBlogCategories();
        if (response.success && response.data) {
          setCategories(response.data);
        } else {
          setCategories([]);
        }
      }
      fetchCategories();
    }
  }, [initialData?.categories]);

  // Load blog posts from API (only if not provided via SSR)
  useEffect(() => {
    if (!initialData?.blogPosts) {
      loadBlogPosts();
    }
  }, [initialData?.blogPosts]);

  const loadBlogPosts = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getPublicBlogs();
      if (response.success && Array.isArray(response.data)) {
        const apiPosts = response.data as ApiBlogPost[];
        const convertedPosts: BlogPost[] = apiPosts.map(apiPost => ({
          id: apiPost._id || '',
          title: apiPost.title,
          excerpt: apiPost.excerpt,
          content: apiPost.content,
          author: apiPost.author,
          publishedDate: apiPost.publishedDate,
          readTime: '5 min read', // Default since backend doesn't provide this
          category: typeof apiPost.category === 'object' ? apiPost.category.name : apiPost.category,
          image: apiPost.image || '/api/placeholder/400/250',
          slug: apiPost.slug
        }));
        setBlogPosts(convertedPosts);
      } else {
        // Fallback to empty if API fails
        setBlogPosts([]);
      }
    } catch (err) {
      console.error('Error loading blog posts:', err);
      setError('Failed to load blog posts');
      setBlogPosts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = blogPosts;

    // Filter by category
    if (selectedCategory !== 'all') {
      const selectedCategoryName = categories.find(cat => cat._id === selectedCategory)?.name;
      filtered = filtered.filter(post => post.category === selectedCategoryName);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.author.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredPosts(filtered);
  }, [blogPosts, selectedCategory, searchQuery, categories]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-600 to-green-800 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              AgriMocks Blog
            </h1>
            <p className="text-xl text-green-100 max-w-3xl mx-auto mb-8">
              Stay updated with the latest insights, tips, and knowledge in agricultural sciences
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search blog posts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-[24px] bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Categories Filter */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            <button
              key="all"
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-[24px] text-sm font-medium transition-colors duration-200 ${
                selectedCategory === 'all'
                  ? 'bg-green-800 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              aria-label="All Posts"
            >
              All Posts
            </button>
            {categories.map((category) => (
              <button
                key={category._id}
                onClick={() => setSelectedCategory(category._id)}
                className={`px-4 py-2 rounded-[24px] text-sm font-medium transition-colors duration-200 ${
                                  selectedCategory === category._id
                  ? 'bg-green-800 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                aria-label={category.name}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-[24px] text-center">
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <Loader variant="fullscreen" text="Loading blog posts..." />
          )}

          {/* Blog Posts Grid */}
          {!loading && filteredPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="block bg-white dark:bg-gray-800 rounded-[24px] shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  <article className="h-full">
                    {/* Post Image */}
                    <div className="aspect-video bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                      {post.image && post.image !== '/api/placeholder/400/250' ? (
                        <img 
                          src={post.image} 
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-green-600 opacity-20"></div>
                      )}
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-[24px]">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    {/* Post Content */}
                    <div className="p-6">
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2">
                        {post.title}
                      </h2>
                      <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>

                      {/* Post Meta */}
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-2" />
                          <span className="font-medium text-gray-700 dark:text-gray-300">{post.author}</span>
                        </div>
                        <span className="mx-2 text-gray-400">•</span>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2" />
                          <span>{formatDate(post.publishedDate)}</span>
                        </div>
                      </div>

                      {/* Read More Link */}
                      <div className="inline-flex items-center text-green-600 dark:text-green-400 font-medium">
                        Read More
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">
                No blog posts found matching your criteria.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
                className="px-4 py-2 bg-green-800 hover:bg-green-900 text-white rounded-24 transition-colors duration-200"
                aria-label="Clear Filters"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage; 